import { useQuery } from "@apollo/client";
import { getMentorshipGroupAddOnPrice } from "@app/shared/prices";
import { ProductFeature, Sangha } from "@app/shared/types";
import { CircularProgress } from "@mui/material";
import useLocalStorage from "@rehooks/local-storage";
import { analyticsTrack } from "app/analytics/track";
import { GRAPHQL_QUERY_AVAILABLE_GROUPS } from "app/queries";
import { routes } from "app/routes";
import { displayPrice } from "components/displayPrice";
import { GenericErrorMessage } from "components/GenericErrorMessage";
import { selectUserProfile } from "features/auth/auth";
import { usePreferredGroup } from "hooks/usePreferredGroup";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { ImmersionGroupCard } from "../community/ImmersionGroupCard";
import { MemberContext } from "../MemberContext";
import { membershipUsesFreeTrialInternally } from "../membership/membershipHelpers";

export const MentorshipGroupList = () => {
    const history = useHistory();
    const userProfile = useSelector(selectUserProfile);

    const preferredGroupId = usePreferredGroup();
    const { memberships } = useContext(MemberContext);
    const membership = memberships[0];
    const [redirectAfterBillingChange, setRedirectAfterBillingChange] = useLocalStorage<
        string | undefined
    >("REDIRECT_AFTER_BILLING_PLAN_CHANGE");

    const mentorshipGroupAddOnDisabled = membershipUsesFreeTrialInternally(membership);

    const {
        data: groupData,
        loading: groupsLoading,
        error: groupsError,
    } = useQuery(GRAPHQL_QUERY_AVAILABLE_GROUPS);

    if (groupsError) {
        return <GenericErrorMessage />;
    }

    let availableGroups: Sangha[] | undefined = groupData?.availableImmersionGroups;

    if (groupsLoading || !availableGroups) {
        return <CircularProgress />;
    }

    availableGroups = availableGroups
        .filter((x) => x.schedule.nextSession)
        .sort((a, b) => b.openSpots - a.openSpots);

    // List preferred group on top
    const preferredGroups = availableGroups.filter((x) => x.id === preferredGroupId);
    if (preferredGroups.length > 0) {
        availableGroups = [
            ...preferredGroups,
            ...availableGroups.filter((x) => x.id !== preferredGroupId),
        ];
    }

    return (
        <>
            {availableGroups.map((group) => (
                <ImmersionGroupCard
                    key={group.id}
                    sangha={group}
                    isAddOnDisabled={mentorshipGroupAddOnDisabled}
                    claimButtonText={
                        <>
                            Add it +
                            {displayPrice(
                                getMentorshipGroupAddOnPrice(group.eligibleMembershipTypes[0]),
                            )}
                            /month
                        </>
                    }
                    handleOnClaimSpot={() => {
                        analyticsTrack("mentorshipGroupList.selectGroupToAdd", {
                            groupId: group.id,
                            teacherName: group.teacher.name,
                            openSpots: group.openSpots,
                        });

                        // If member is on a legacy plan, ask them to switch to new pricing first, since we can't compute a price for them
                        if (
                            !membership.billingChoice.plan.features.includes(
                                ProductFeature.ELIGIBLE_FOR_ADD_ONS,
                            )
                        ) {
                            setRedirectAfterBillingChange(routes.addMentorshipGroup(group.id));
                            history.push(routes.memberUpdateLegacyBillingPage1());
                        } else {
                            history.push(routes.addMentorshipGroup(group.id));
                        }
                    }}
                />
            ))}
        </>
    );
};
